'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ServiceSpecializationProps {
  onDataChange: (data: ServiceSpecializationData) => void;
  initialData?: ServiceSpecializationData;
}

export interface ServiceSpecializationData {
  brands: string[];
  services: string[];
  certifications: string[];
  specialNotes: string;
}

const ServiceSpecialization = ({ onDataChange, initialData }: ServiceSpecializationProps) => {
  const [formData, setFormData] = useState<ServiceSpecializationData>(
    initialData || {
      brands: [],
      services: [],
      certifications: [],
      specialNotes: ''
    }
  );

  const brandOptions = [
    { value: 'BMW', label: 'BMW', icon: 'TruckIcon' },
    { value: 'Lexus', label: 'Lexus', icon: 'TruckIcon' },
    { value: 'Mercedes-Benz', label: 'Mercedes-Benz', icon: 'TruckIcon' },
    { value: 'Audi', label: 'Audi', icon: 'TruckIcon' },
    { value: 'Acura', label: 'Acura', icon: 'TruckIcon' },
    { value: 'Infiniti', label: 'Infiniti', icon: 'TruckIcon' }
  ];

  const serviceOptions = [
    { value: 'Oil Change', label: 'Oil Change & Maintenance', description: 'Routine oil changes and fluid checks' },
    { value: 'Brake Service', label: 'Brake Service & Repair', description: 'Brake pads, rotors, and system diagnostics' },
    { value: 'Engine Diagnostics', label: 'Engine Diagnostics', description: 'Computer diagnostics and check engine light' },
    { value: 'Transmission', label: 'Transmission Service', description: 'Transmission fluid, repair, and replacement' },
    { value: 'Suspension', label: 'Suspension & Steering', description: 'Struts, shocks, and alignment services' },
    { value: 'Electrical', label: 'Electrical Systems', description: 'Battery, alternator, and wiring repairs' },
    { value: 'AC Repair', label: 'AC & Heating', description: 'Climate control system service' },
    { value: 'Tire Service', label: 'Tire Service', description: 'Tire rotation, balancing, and replacement' }
  ];

  const certificationOptions = [
    { value: 'ASE', label: 'ASE Certified', description: 'Automotive Service Excellence certification' },
    { value: 'BMW', label: 'BMW Specialist', description: 'Factory-trained BMW technician' },
    { value: 'Lexus', label: 'Lexus Specialist', description: 'Factory-trained Lexus technician' },
    { value: 'Bosch', label: 'Bosch Certified', description: 'Bosch automotive service certification' },
    { value: 'AAA', label: 'AAA Approved', description: 'AAA approved auto repair facility' }
  ];

  const handleBrandToggle = (brand: string) => {
    const updatedBrands = formData.brands.includes(brand)
      ? formData.brands.filter(b => b !== brand)
      : [...formData.brands, brand];
    
    const updatedData = { ...formData, brands: updatedBrands };
    setFormData(updatedData);
    onDataChange(updatedData);
  };

  const handleServiceToggle = (service: string) => {
    const updatedServices = formData.services.includes(service)
      ? formData.services.filter(s => s !== service)
      : [...formData.services, service];
    
    const updatedData = { ...formData, services: updatedServices };
    setFormData(updatedData);
    onDataChange(updatedData);
  };

  const handleCertificationToggle = (cert: string) => {
    const updatedCerts = formData.certifications.includes(cert)
      ? formData.certifications.filter(c => c !== cert)
      : [...formData.certifications, cert];
    
    const updatedData = { ...formData, certifications: updatedCerts };
    setFormData(updatedData);
    onDataChange(updatedData);
  };

  const handleNotesChange = (notes: string) => {
    const updatedData = { ...formData, specialNotes: notes };
    setFormData(updatedData);
    onDataChange(updatedData);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-start space-x-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 flex-shrink-0">
          <Icon name="WrenchScrewdriverIcon" size={20} className="text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-lg font-semibold text-text-primary mb-1">
            Service Specialization
          </h3>
          <p className="font-body text-sm text-text-secondary">
            Select the brands and services your shop specializes in
          </p>
        </div>
      </div>

      {/* Brand Specialization */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-caption text-xs font-medium text-text-secondary uppercase tracking-wider">
            Brand Specialization <span className="text-error">*</span>
          </h4>
          <span className="font-caption text-xs text-muted-foreground">
            {formData.brands.length} selected
          </span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {brandOptions.map(brand => (
            <button
              key={brand.value}
              type="button"
              onClick={() => handleBrandToggle(brand.value)}
              className={`flex items-center space-x-3 rounded-lg border-2 px-4 py-3 transition-smooth ${
                formData.brands.includes(brand.value)
                  ? 'border-primary bg-primary/5 text-primary' :'border-border bg-card text-text-secondary hover:border-primary/50 hover:bg-muted'
              }`}
            >
              <Icon name={brand.icon as any} size={20} />
              <span className="font-body text-sm font-medium">{brand.label}</span>
              {formData.brands.includes(brand.value) && (
                <Icon name="CheckCircleIcon" size={18} variant="solid" className="ml-auto" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Services Offered */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-caption text-xs font-medium text-text-secondary uppercase tracking-wider">
            Services Offered <span className="text-error">*</span>
          </h4>
          <span className="font-caption text-xs text-muted-foreground">
            {formData.services.length} selected
          </span>
        </div>
        <div className="space-y-2">
          {serviceOptions.map(service => (
            <label
              key={service.value}
              className={`flex items-start space-x-3 rounded-lg border px-4 py-3 cursor-pointer transition-smooth ${
                formData.services.includes(service.value)
                  ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-primary/50 hover:bg-muted'
              }`}
            >
              <input
                type="checkbox"
                checked={formData.services.includes(service.value)}
                onChange={() => handleServiceToggle(service.value)}
                className="mt-0.5 h-5 w-5 rounded border-border text-primary transition-smooth focus:ring-2 focus:ring-primary focus:ring-offset-2"
              />
              <div className="flex-1">
                <div className="font-body text-sm font-medium text-text-primary">
                  {service.label}
                </div>
                <div className="font-caption text-xs text-text-secondary mt-0.5">
                  {service.description}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Certifications */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-caption text-xs font-medium text-text-secondary uppercase tracking-wider">
            Certifications (Optional)
          </h4>
          <span className="font-caption text-xs text-muted-foreground">
            {formData.certifications.length} selected
          </span>
        </div>
        <div className="space-y-2">
          {certificationOptions.map(cert => (
            <label
              key={cert.value}
              className={`flex items-start space-x-3 rounded-lg border px-4 py-3 cursor-pointer transition-smooth ${
                formData.certifications.includes(cert.value)
                  ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-primary/50 hover:bg-muted'
              }`}
            >
              <input
                type="checkbox"
                checked={formData.certifications.includes(cert.value)}
                onChange={() => handleCertificationToggle(cert.value)}
                className="mt-0.5 h-5 w-5 rounded border-border text-primary transition-smooth focus:ring-2 focus:ring-primary focus:ring-offset-2"
              />
              <div className="flex-1">
                <div className="font-body text-sm font-medium text-text-primary">
                  {cert.label}
                </div>
                <div className="font-caption text-xs text-text-secondary mt-0.5">
                  {cert.description}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Special Notes */}
      <div className="space-y-2">
        <label htmlFor="specialNotes" className="font-caption text-xs font-medium text-text-secondary uppercase tracking-wider block">
          Additional Information (Optional)
        </label>
        <textarea
          id="specialNotes"
          value={formData.specialNotes}
          onChange={(e) => handleNotesChange(e.target.value)}
          placeholder="Describe any unique services, specializations, or equipment your shop offers..."
          rows={4}
          className="w-full rounded-lg border border-border bg-input px-4 py-3 text-sm text-text-primary placeholder:text-muted-foreground transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 resize-none"
        />
        <p className="font-caption text-xs text-muted-foreground">
          This information helps customers understand your unique capabilities
        </p>
      </div>
    </div>
  );
};

export default ServiceSpecialization;